/**
 * Standalone Broadcasts List Component
 *
 * Professional list view for standalone broadcasts with:
 * - Infinite scroll
 * - Clean list design matching Campaigns
 * - Search capabilities
 * - Status badges and actions
 * - Responsive layout
 *
 * @component
 */

import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Skeleton, Tooltip, Popover } from 'antd';
import { PlusOutlined, ShopOutlined, FormOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';

// assets
import Delete from '../../../assets/customSVG/Delete';
import Edit from '../../../assets/customSVG/Edit';
import Refresh from '../../../assets/customSVG/Refresh';
import ClearFilters from '../../../assets/customSVG/ClearFilters';
import { images } from '../../../assets/images/index';

// helpers
import { cn } from '../../../helper/cn';
import { exportBroadcastReport } from '../../../helper/excelExport';
import { useAspSelector, useAspDispatch } from '../../../test/jest-redux-hooks';
import { setExportPolling } from '../../../reduxToolkit/broadcastSlice';

// api
import {
  fetchBroadcastsList,
  deleteBroadcast as deleteStandaloneBroadcast,
  retryBroadcast,
  fetchBroadcastContactList,
  exportReportStatus,
  exportReportUserSeen,
} from '../api/broadcastsApi';

// hooks
import useExportReportPolling from '../../../hooks/useExportReportPolling';

// constants
import { CAMPAIGN_STATUS } from '../../campaigns/constants/campaigns';
import { STATUS, SORT } from '../constants/broadcastList';

// components
import Loader from '../../../components/commonComponents/Loader/Index';
import NoData from '../../../components/commonComponents/Empty/NoData';
import SearchBar from '../../../components/commonComponents/searchBar';
import CheckBoxSqure from '../../../components/commonComponents/checkbox/CheckBoxSqure';
import NewRadioButton from '../../../components/form/radio/NewRadio';
import { ICONS } from '../../../assets/icons'; // Assuming this path exists based on List.jsx relative path

import NotificationContext from '../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../components/commonComponents/gxAntdToast/toastUtils';

const BroadcastsList = () => {
  const navigate = useNavigate();
  const dispatch = useAspDispatch();
  const { currentBrand } = useAspSelector((state) => state.app);
  const isExportPolling = useAspSelector((state) => state.broadcast.isExportPolling);
  const listContainerRef = useRef(null);
  const notification = useContext(NotificationContext);

  // State management
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Filter and Sort states
  const [status, setStatus] = useState(STATUS);
  const [sort, setSort] = useState(SORT);
  const [sortLabel, setSortLabel] = useState('Newest First');

  useExportReportPolling({
    isPolling: isExportPolling,
    fetchStatus: () =>
      exportReportStatus(currentBrand?.brand_id, 'broadcast_contacts'),
    markSeen: (reportId) =>
      exportReportUserSeen(currentBrand?.brand_id, {
        export_report_id: reportId,
      }),
    onComplete: () => {
      showSuccessToast(
        notification,
        'Report is ready! A download link has been sent to your email.',
        5,
      );
    },
    onError: () => {
      showErrorToast(
        notification,
        'Report generation failed. Please try again.',
        5,
      );
    },
    onStop: () => dispatch(setExportPolling(false)),
  });

  // Modal state for creation
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

  const handleCreateOpen = () => navigate('/user/broadcasts/new');
  const handleCreateCancel = () => setIsCreateModalVisible(false);

  /**
   * Fetch broadcasts from API
   */
  const fetchBroadcasts = useCallback(
    async (isLoadMore = false) => {
      if (!currentBrand?.brand_id) return;

      try {
        if (!isLoadMore) {
          setLoading(true);
          setBroadcasts([]); // Clear list to show Skeleton loader
          setPage(1);
          setHasMore(true);
        }

        const currentPage = isLoadMore ? page + 1 : 1;

        // Extract active filters
        const statusFilter = status
          .filter((s) => s.isSelected)
          .map((s) => s.status)
          .join(',');

        // Extract active sort (single selection across all sections)
        const selectedOption = sort
          .flatMap((s) => s.options)
          .find((o) => o.isSelected);

        const sortBy = selectedOption?.field || 'created_at';
        const sortOrder = selectedOption?.status || 'desc';

        const response = await fetchBroadcastsList(currentBrand.brand_id, {
          page: currentPage,
          limit: 20,
          search: searchKey,
          status: statusFilter,
          sortBy: sortBy,
          sortOrder: sortOrder,
        });

        if (response?.status === 200) {
          const data = response.data?.broadcasts || [];
          const totalCount = response.data?.total || 0;

          if (isLoadMore) {
            setBroadcasts((prev) => [...prev, ...data]);
            setPage(currentPage);
          } else {
            setBroadcasts(data);
            setPage(1);
          }

          setTotal(totalCount);
          // If we fetched fewer items than limit (20), or if total reached, no more items
          if (
            data.length < 20 ||
            (isLoadMore && broadcasts.length + data.length >= totalCount)
          ) {
            setHasMore(false);
          } else {
            // Check if we start with less than total
            setHasMore(
              data.length > 0 &&
                (isLoadMore
                  ? broadcasts.length + data.length < totalCount
                  : data.length < totalCount),
            );
          }
        } else {
          showErrorToast(
            notification,
            response?.response?.data?.error || 'Failed to load broadcasts',
          );
          setHasMore(false);
        }
      } catch (error) {
        showErrorToast(notification, 'Failed to load broadcasts');
        setHasMore(false);
        console.error('Error fetching broadcasts:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      currentBrand?.brand_id,
      page,
      searchKey,
      broadcasts.length,
      notification,
      status,
      sort,
    ],
  );

  useEffect(() => {
    // Initial load or search/filter/sort change
    fetchBroadcasts(false);
  }, [searchKey, status, sort, currentBrand?.brand_id]);

  const loadNextPage = () => {
    fetchBroadcasts(true);
  };

  /**
   * Handle search input change
   */
  const handleSearch = (value) => {
    setSearchKey(value);
  };

  const onStatusChange = (item) => {
    setStatus((prev) =>
      prev.map((s) =>
        s.id === item.id ? { ...s, isSelected: !s.isSelected } : s,
      ),
    );
  };

  const clearStatus = () => {
    setStatus((prev) => prev.map((s) => ({ ...s, isSelected: false })));
  };

  const onSortChange = (sectionId, item) => {
    setSort((prev) =>
      prev.map((section) => ({
        ...section,
        options: section.options.map((option) => ({
          ...option,
          isSelected: option.id === item.id,
        })),
      })),
    );
    setSortLabel(item.label);
  };

  const allowAllClear = () => {
    return status.some((s) => s.isSelected);
  };

  /**
   * Handle broadcast deletion
   */
  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Broadcast',
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteStandaloneBroadcast(record.id, currentBrand?.brand_id)
          .then((res) => {
            if (res?.status === 200) {
              showSuccessToast(notification, 'Broadcast deleted successfully');
              fetchBroadcasts(false); // Reload list
            } else {
              showErrorToast(notification, 'Failed to delete broadcast');
            }
          })
          .catch((error) => {
            showErrorToast(notification, 'Failed to delete broadcast');
            console.error('Error deleting broadcast:', error);
          });
      },
    });
  };

  /**
   * Handle broadcast retry
   */
  const handleRetry = async (record) => {
    try {
      const res = await retryBroadcast(record.id, currentBrand.brand_id);
      if (res?.status === 200) {
        showSuccessToast(notification, 'Broadcast retry initiated');
        fetchBroadcasts(false);
      } else {
        showErrorToast(
          notification,
          res?.data?.error || res?.response?.data?.error || 'Reactivate failed',
        );
      }
    } catch (error) {
      showErrorToast(
        notification,
        error?.response?.data?.error || 'Failed to retry broadcast',
      );
      console.error('Error retrying broadcast:', error);
    }
  };

  /**
   * Handle broadcast edit
   */
  const handleEdit = (record) => {
    navigate(`/user/broadcasts/${record.id}/edit`);
  };

  /**
   * Handle broadcast report download
   *
   * Downloads a comprehensive Excel report for a broadcast including:
   * - Summary section at the top with key metrics (Total, Delivered, Opened, Lead, Clicked)
   * - Detailed contact table with all message data
   *
   * The report is generated using the centralized excelExport helper module
   * which handles all formatting, styling, and file generation.
   *
   * @param {Object} record - The broadcast record to generate report for
   * @param {number} record.id - Broadcast ID
   * @param {string} record.name - Broadcast name (used for filename)
   */
  const handleDownloadReport = async (record) => {
    try {
      setLoading(true);

      const response = await fetchBroadcastContactList(
        currentBrand.brand_id,
        record.id,
        { download_all: true },
      );

      if (response?.status === 200) {
        // Large file — dispatched to background worker
        if (response.data?.export_dispatched) {
          if (response.data?.already_in_progress) {
            showErrorToast(
              notification,
              response.data.message || 'A report is already being generated.',
              5,
            );
          } else {
            showSuccessToast(
              notification,
              response.data.message ||
                'Report is being generated. You will receive an email with the download link.',
              5,
            );
          }
          dispatch(setExportPolling(true));
          return;
        }

        // Small file — generate Excel client-side (existing flow)
        const allContacts = response.data?.broadcast_contacts || [];
        const statusCounts = response.data?.status_counts || {};

        const result = await exportBroadcastReport({
          contacts: allContacts,
          counts: {
            total: statusCounts.total || allContacts.length,
            delivered: statusCounts.delivered || 0,
            opened: statusCounts.opened || 0,
            lead: statusCounts.lead || 0,
            clicked: statusCounts.clicked || 0,
            bounced: statusCounts.bounced || 0,
          },
          fileName: record.name,
          sheetName: 'Broadcast Report',
        });

        if (result.success) {
          showSuccessToast(notification, result.message);
        } else {
          showErrorToast(notification, result.message);
        }
      } else {
        showErrorToast(notification, 'Failed to download report');
      }
    } catch (error) {
      showErrorToast(notification, 'Error downloading report');
      console.error('Error downloading report:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle row click to view details or metrics
   * Status 0 = Draft - navigate to view details
   * Status 8, 10, 11, 12 = Stopped, Scheduled, Running, Completed - navigate to metrics
   */
  const handleNavigateBroadcast = (record) => {
    // For draft broadcasts (status 0), navigate to view details
    // For all other statuses (8, 10, 11, 12), navigate to metrics
    if ([0, 10].includes(record.status)) {
      navigate(`/user/broadcasts/${record.id}`);
    } else {
      navigate(`/user/broadcasts/${record.id}/metrics`);
    }
  };

  // styles
  const bodyStyle =
    'text-sm weight-medium text-[var(--font-600)] text-left pl-2';

  return (
    <div className="w-full min-h-screen relative flex flex-col gap-10 p-20 pb-0 bg-[#F9FAFB] bg-[radial-gradient(circle_at_18%_10%,rgba(10,227,176,0.14),transparent_32%),radial-gradient(circle_at_82%_12%,rgba(124,58,237,0.12),transparent_30%),linear-gradient(180deg,#f9f9fc_0%,#f7f8fb_60%,#f5f7fb_100%)]">
      {/* Header Section */}
      <div className="w-100 flex items-center justify-between my-7">
        <div className="w-[65%] flex-row align-center gap-5">
          <img src={images?.Flag} alt="flag" />
          <div className=" flex-column gap-2.5">
            <h2 className="text-3xl weight-semibold">Broadcasts</h2>
            <p className="header-titles ">
              Reach your audience instantly. Create broadcast messages, schedule
              them for the perfect time, and track delivery and engagement
              metrics in real-time.
            </p>
          </div>
        </div>
        <div
          className="flex-row align-center px-4 py-2 text-base weight-medium rounded-md !bg-[var(--new-primary)] border-2 border-[var(--new-primary)] text-white cursor-pointer transition-colors shadow-sm"
          onClick={handleCreateOpen}
        >
          <img src={ICONS?.CreateCampaignNew} alt="Create" className="pr-3" />
          <span>Create Broadcast</span>
        </div>
      </div>

      <div className="w-full h-full bg-white rounded-t-2xl px-5 flex flex-col flex-1 shadow-sm border border-[#E8E8EA]">
        {/* Filter/Search Section inside the white box */}
        <div className="w-full py-5 flex items-center justify-between gap-5 border-b border-[var(--border-50)]">
          <h4 className="new-header3">
            {total > 0 ? `${total} Total Broadcasts` : 'Broadcasts'}
          </h4>
          <div className="flex items-center gap-5">
            <div>
              <SearchBar
                searchKey={searchKey}
                setSearchKey={setSearchKey}
                onChange={handleSearch}
                loading={false}
                expandable={false}
              />
            </div>
            <Popover
              trigger={['click']}
              placement="bottom"
              arrow={false}
              content={
                <div className="min-w-56 px-3.5 py-2.5">
                  {sort?.map((section) => (
                    <div key={section?.heading}>
                      <h3 className="new-header1 mb-2 pt-5">
                        {section.heading}
                      </h3>
                      {section?.options?.map((s) => (
                        <div
                          key={s.id}
                          className={cn(
                            'w-full h-9 flex items-center gap-2.5 py-1 ',
                            'cursor-pointer opacity-100',
                          )}
                          onClick={() => {
                            onSortChange(section?.id, s);
                          }}
                        >
                          <div className="w-5 h-9 flex items-center justify-center mt-1">
                            <NewRadioButton
                              value={s?.isSelected}
                              selected={s?.isSelected}
                              onChange={(e) => {}}
                              rootClassName={
                                s?.isSelected && 'size-3.5 new-radio'
                              }
                            />
                          </div>
                          {s?.icon && <img src={s?.icon} alt={s.label} />}
                          <p className="font-medium text-sm text-[#202421]">
                            {s?.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              }
            >
              <div className="group/sort flex items-center gap-2.5 bg-[#F9FAFB] border border-[#E8E8EA] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-[#616874]">
                  {sortLabel || 'Newest First'}
                </div>
                <div className="border-[4px] border-transparent border-t-[#616874] translate-y-[2px]"></div>
              </div>
            </Popover>

            <Popover
              trigger={['click']}
              arrow={false}
              placement="bottom"
              content={
                <div className="min-w-56 px-3.5 py-2.5">
                  <div className="w-full h-8 flex items-center justify-between gap-5 py-2.5 text-sm weight-medium">
                    <div className="text-[var(--font-400)] text-[#616874]">
                      Status
                    </div>
                    <div
                      className={cn(
                        'text-[#F96056]',
                        status.some((s) => s.isSelected)
                          ? 'cursor-pointer opacity-100'
                          : 'cursor-not-allowed opacity-50',
                      )}
                      onClick={() => {
                        if (status.some((s) => s.isSelected)) clearStatus();
                      }}
                    >
                      Clear
                    </div>
                  </div>
                  {status?.map((s) => {
                    return (
                      <div
                        className={cn(
                          'w-full h-9 flex items-center gap-2.5 py-1 cursor-pointer',
                        )}
                        onClick={() => {
                          onStatusChange(s);
                        }}
                        key={s?.id}
                      >
                        <CheckBoxSqure
                          value={s?.isSelected}
                          onChange={(e) => {}}
                          className="size-4"
                        />
                        {s?.icon && (
                          <img src={s?.icon} alt={s.label} className="w-4" />
                        )}
                        <p className="font-medium text-sm text-[#202421]">
                          {s?.label}
                        </p>
                      </div>
                    );
                  })}
                </div>
              }
            >
              <div className="group/goal flex items-center gap-2.5 bg-[#F9FAFB] border border-[#E8E8EA] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-[#616874] flex items-center">
                  Status
                  {status.some((s) => s.isSelected) && (
                    <div className="ml-1.5 rounded-full bg-[#6940F2] text-white text-[10px] w-4 h-4 flex justify-center items-center font-bold">
                      {status.filter((s) => s.isSelected).length}
                    </div>
                  )}
                </div>
                <div className="border-[4px] border-transparent border-t-[#616874] translate-y-[2px]"></div>
              </div>
            </Popover>

            <div
              className={cn(
                'group/status flex items-center gap-2.5 hover:bg-gray-50 px-2.5 py-2.5 rounded-lg transition-colors',
                allowAllClear()
                  ? 'cursor-pointer opacity-100'
                  : 'cursor-not-allowed opacity-50',
              )}
              onClick={() => {
                if (allowAllClear()) {
                  clearStatus();
                }
              }}
            >
              <ClearFilters
                width={16}
                height={16}
                color={allowAllClear() ? '#202421' : '#616874'}
              />
            </div>
          </div>
        </div>

        <div className="w-full relative h-full overflow-hidden flex-1">
          <Skeleton
            loading={loading && broadcasts.length === 0}
            active
            paragraph={{ rows: 8 }}
          >
            {broadcasts?.length > 0 && (
              <div className="w-full h-14 sticky top-0 flex items-center pr-1 border-b border-gray-100 bg-white z-10">
                <div className={cn('new-header2', 'w-[30%] pl-3')}>
                  Broadcast Name
                </div>
                <div className={cn('new-header2', 'w-[13%] text-center')}>
                  Status
                </div>
                <div className={cn('new-header2', 'w-[12%] text-center')}>
                  Total Contacts
                </div>
                <div className={cn('new-header2', 'w-[9%] text-center')}>
                  Sent %
                </div>
                <div className={cn('new-header2', 'w-[9%] text-center')}>
                  Delivered %
                </div>
                <div className={cn('new-header2', 'w-[12%] text-center')}>
                  Open Rate %
                </div>
                <div className={cn('new-header2', 'w-[15%] text-center')}>
                  Amount Spent
                </div>
              </div>
            )}

            <div
              className={`min-w-full h-full min-h-[60vh] overflow-hidden overflow-y-scroll`}
              id="scrollableDiv"
              ref={listContainerRef}
            >
              <InfiniteScroll
                dataLength={broadcasts?.length || 0}
                next={loadNextPage}
                hasMore={hasMore}
                scrollableTarget="scrollableDiv"
                height={loading ? '58vh' : '63vh'}
                className="!pr-1 max-[1600px]:pb-24 min-[1600px]:pb-24 listScroll"
                loader={
                  <div className="w-full h-[5vh] flex items-center justify-center bg-white rounded-lg mt-2">
                    <Loader Width={30} Height={30} />
                  </div>
                }
              >
                {broadcasts?.length > 0 ? (
                  <>
                    {broadcasts?.map((l) => {
                      const status =
                        CAMPAIGN_STATUS[l?.status] || CAMPAIGN_STATUS[0];

                      return (
                        <div
                          key={l?.id}
                          className="w-full odd:bg-gray-50 my-1 py-4 flex items-center rounded-lg pointer hover:bg-gray-100 transition-colors group/container"
                        >
                          {/* Name & Date & Actions */}
                          <div
                            className={cn(
                              bodyStyle,
                              'w-[30%] cursor-pointer pl-3 flex flex-col gap-1 pr-4 group',
                            )}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            <div className="flex items-center gap-2">
                              {l?.name?.length > 25 ? (
                                <Tooltip title={l?.name} placement="top">
                                  <div className="new-header truncate max-w-[25ch] font-semibold text-[#202421]">
                                    {l?.name
                                      ?.trim()
                                      ?.charAt(0)
                                      .toUpperCase() +
                                      l?.name?.trim()?.slice(1).toLowerCase()}
                                  </div>
                                </Tooltip>
                              ) : (
                                <div className="new-header font-semibold text-[#202421] flex gap-2">
                                  {l?.name && (
                                    <div>
                                      {l.name
                                        ?.trim()
                                        ?.charAt(0)
                                        .toUpperCase() +
                                        l.name
                                          ?.trim()
                                          ?.slice(1)
                                          .toLowerCase()}
                                    </div>
                                  )}
                                  {l?.status === 8 && status?.icon && (
                                    <Tooltip
                                      title={
                                        Array.isArray(l?.error_notes)
                                          ? JSON.stringify(l.error_notes)
                                          : l?.error_notes?.error ||
                                            l?.error_notes
                                      }
                                    >
                                      <img
                                        src={status?.alert_icon}
                                        alt={status?.text}
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Date & Action Icons Row */}
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-gray-500">
                                Created @{' '}
                                {dayjs(l?.created_at).format('MMM DD, h:mm A')}
                              </div>
                              {/* Action Buttons - Inline with date */}
                              <div className="flex items-center gap-1 opacity-0 group-hover/container:opacity-100 transition-opacity duration-200">
                                {/* Analytics Action - Only for 11, 12 */}
                                {[11, 12].includes(l?.status) && (
                                  <Tooltip title="Analytics" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleNavigateBroadcast(l);
                                      }}
                                    >
                                      <img
                                        src={ICONS?.analytics}
                                        alt="analytics"
                                        className="w-4 h-4"
                                      />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Download Report Action - Only for 11, 12 */}
                                {[11, 12].includes(l?.status) && (
                                  <Tooltip
                                    title="Download Report"
                                    placement="top"
                                  >
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadReport(l);
                                      }}
                                    >
                                      <img
                                        src={ICONS?.Download}
                                        alt="download"
                                        className="w-4 h-4"
                                      />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Edit Action */}
                                {[0, 10].includes(l?.status) && (
                                  <Tooltip title="Edit" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(l);
                                      }}
                                    >
                                      <Edit
                                        width="16px"
                                        height="16px"
                                        strokeWidth="1.7"
                                        className="stroke-[#898E99] hover:stroke-[var(--contentText)]"
                                      />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Retry Action */}
                                {l?.status === 8 && (
                                  <Tooltip title="Retry" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRetry(l);
                                      }}
                                    >
                                      <Refresh
                                        width="16px"
                                        height="16px"
                                      />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Delete Action */}
                                {[0, 10].includes(l?.status) && (
                                  <Tooltip title="Delete" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 transition-colors group/delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(l);
                                      }}
                                    >
                                      <Delete
                                        width="16px"
                                        height="16px"
                                        className="[&>path]:stroke-[#898e99] [&>path]:transition-colors group-hover/delete:[&>path]:!stroke-[#ef4444]"
                                      />
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status */}
                          <div
                            className={cn(
                              bodyStyle,
                              'w-[13%] flex items-center justify-center gap-1.5',
                            )}
                            style={{ color: status?.color }}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            <div
                              style={{
                                background: status?.background,
                                border: `1px solid ${status?.border}`,
                              }}
                              className="flex-row align-center rounded-full  p-1 pl-10 pr-10 gap-1.5"
                              onClick={() => {
                                handleNavigateBroadcast(l);
                              }}
                            >
                              {status?.icon && (
                                <img src={status?.icon} alt={status?.text} />
                              )}
                              {status?.text}
                            </div>
                          </div>

                          {/* Total Contacts */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[12%] text-center',
                            )}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            {l?.status === 0
                              ? '--'
                              : l?.total_contacts?.toLocaleString() || '0'}
                          </div>

                          {/* Sent % */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[9%] text-center',
                              l?.status !== 0 && 'text-[#0F9960]',
                            )}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            {l?.status === 0
                              ? '--'
                              : l?.sent_percentage
                                ? `${l.sent_percentage.toFixed(1)}%`
                                : '0%'}
                          </div>

                          {/* Delivered % */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[9%] text-center',
                              l?.status !== 0 && 'text-[#0F9960]',
                            )}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            {l?.status === 0
                              ? '--'
                              : l?.delivered_percentage
                                ? `${l.delivered_percentage.toFixed(1)}%`
                                : '0%'}
                          </div>

                          {/* Open Rate */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[12%] text-center',
                              l?.status !== 0 && 'text-[#6940F2]',
                            )}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            {l?.status === 0
                              ? '--'
                              : l?.open_rate
                                ? `${l.open_rate.toFixed(1)}%`
                                : '0%'}
                          </div>

                          {/* Amount Spent */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[15%] text-center text-[#202421] font-medium',
                            )}
                            onClick={() => handleNavigateBroadcast(l)}
                          >
                            {l?.status === 0
                              ? '--'
                              : l?.amount_spent
                                ? `₹${l.amount_spent.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                : '₹0.00'}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {!searchKey ? (
                      <div className="w-full h-full flex justify-center mt-20">
                        <NoData
                          title="No broadcasts yet"
                          content="Create your first broadcast to start reaching your customers"
                          modal={
                            <div className="flex gap-3">
                              <button
                                className="flex-row align-center px-4 py-1.5 text-base weight-medium rounded-md bg-[var(--new-primary)] border-2 border-[var(--new-primary)] text-white transition-colors"
                                onClick={() => navigate('/user/broadcasts/new')}
                              >
                                <PlusOutlined className="mr-2" />
                                Create Broadcast
                              </button>
                            </div>
                          }
                          icon={ICONS?.campaignNoData} // Using campaign icon as fallback/standard
                        />
                      </div>
                    ) : (
                      <div className="h-[60vh] flex justify-center mt-20 pt-20">
                        <div className="flex-row h-10 mt-20">
                          <img
                            src={ICONS?.emptySearch}
                            alt="emptySearch"
                            className="w-14"
                          />
                          <div className="flex-column gap-3 pl-10">
                            <span className="new-header3 ">
                              Sorry, there are no matching results
                            </span>
                            <span className="no-search-body">
                              Use different keywords or{' '}
                              <span
                                className="text-[#6940F2] pointer"
                                onClick={() => {
                                  setSearchKey('');
                                  clearStatus();
                                }}
                              >
                                reset filter.
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </InfiniteScroll>
            </div>
          </Skeleton>
        </div>
      </div>

      {/* Create Broadcast Modal - Clean design matching reference */}
      {/* <Modal
        open={isCreateModalVisible}
        onCancel={handleCreateCancel}
        footer={null}
        closable={false}
        centered
        width={720}
        styles={{
          content: { padding: 0, borderRadius: '16px', overflow: 'hidden' },
        }}
      >
        <div className="flex items-center justify-between px-6 py-4 bg-[#E6FFEB] border-b border-[#D1F7D6]">
          <h2 className="text-xl font-semibold text-[#06441C]">
            Create a broadcast
          </h2>
          <button
            onClick={handleCreateCancel}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#D1F7D6] transition-colors"
          >
            <span className="text-[#06441C] text-xl leading-none">&times;</span>
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm font-medium text-[#202421] mb-4">
            Select broadcast type
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div
              className="flex items-center gap-4 p-4 bg-[#F5F3FF] border border-[#E9E5FF] rounded-xl cursor-pointer hover:border-[#A78BFA] hover:shadow-sm transition-all group"
              onClick={() => {
                handleCreateCancel();
                navigate('/user/broadcasts/new?type=custom');
              }}
            >
              <div className="w-16 h-16 flex-shrink-0 bg-[#EDE9FE] rounded-xl flex items-center justify-center">
                <FormOutlined style={{ fontSize: '28px', color: '#8B5CF6' }} />
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="text-base font-semibold text-[#202421] group-hover:text-[#8B5CF6] transition-colors">
                  Custom Broadcast
                </h4>
                <p className="text-xs text-[#616874] leading-relaxed">
                  Design your own message with full control over content and
                  audience.
                </p>
              </div>
            </div>

            <div
              className="flex items-center gap-4 p-4 bg-[#FFF8F0] border border-[#FFE8D4] rounded-xl cursor-pointer hover:border-[#FFB870] hover:shadow-sm transition-all group"
              onClick={() => {
                handleCreateCancel();
                navigate('/user/broadcasts/new?type=sales');
              }}
            >
              <div className="w-16 h-16 flex-shrink-0 bg-[#FFEDD5] rounded-xl flex items-center justify-center">
                <ShopOutlined style={{ fontSize: '28px', color: '#F97316' }} />
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="text-base font-semibold text-[#202421] group-hover:text-[#F97316] transition-colors">
                  Sales Broadcast
                </h4>
                <p className="text-xs text-[#616874] leading-relaxed">
                  Send payment templates and track sales metrics for your
                  Broadcast.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal> */}
    </div>
  );
};

export default BroadcastsList;
